import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { LoadingContext } from "../LoadingContext";
import Loading from "./Loading";
import "./AdminVoucherAnalytics.css";
import Swal from "sweetalert2";
import { toLocalDateString } from "../dateUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MySwal = Swal.mixin({
  customClass: {
    container: "my-swal-container",
  },
});

function AdminVoucherAnalytics() {
  const { authFetch } = useContext(AuthContext);
  const { setIsLoading } = useContext(LoadingContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);

  const toggleExportDropdown = () => {
    setIsExportDropdownOpen(!isExportDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = document.getElementsByClassName('export-dropdown-container');
      for (let i = 0; i < dropdowns.length; i++) {
        if (isExportDropdownOpen && !dropdowns[i].contains(event.target)) {
          setIsExportDropdownOpen(false);
        }
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportDropdownOpen]);

  const [voucherData, setVoucherData] = useState({
    totalRedemptions: 0,
    totalDiscountAmount: 0,
    averageDiscountPerOrder: 0,
    totalVouchers: 0,
    activeVouchers: 0
  });

  const [dateRange, setDateRange] = useState("last30days");
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [selectedVoucherCode, setSelectedVoucherCode] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const [filteredRedemptionHistory, setFilteredRedemptionHistory] = useState([]);
  const [redemptionListView, setRedemptionListView] = useState("last30days");
  const [redemptionListPage, setRedemptionListPage] = useState(1);
  const [redemptionListPerPage] = useState(10);
  const [redemptionListSummary, setRedemptionListSummary] = useState({
    totalRedemptions: 0,
    totalOriginalAmount: 0,
    totalDiscountAmount: 0,
    totalFinalAmount: 0
  });

  const formatCurrency = (amount) => {
    return `RM${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  // DD/MM/YYYY HH:mm - toLocaleString() renders US format (7/29/26, 5:24 PM), which
  // is wrong for this report.
  const formatDateTime = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date)) return "N/A";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const getDateRangeParams = () => {
    const today = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case "today":
        startDate = today;
        endDate = today;
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = startDate;
        break;
      case "last7days":
        endDate = today;
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        break;
      case "last30days":
        endDate = today;
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        break;
      case "monthToDate":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case "quarterToDate":
        const quarter = Math.floor(today.getMonth() / 3);
        startDate = new Date(today.getFullYear(), quarter * 3, 1);
        endDate = today;
        break;
      case "yearToDate":
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
        break;
      case "custom":
        startDate = customDateRange.startDate ? new Date(customDateRange.startDate) : null;
        endDate = customDateRange.endDate ? new Date(customDateRange.endDate) : null;
        break;
      default:
        endDate = today;
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
    }

    return {
      startDate: startDate ? toLocalDateString(startDate) : null,
      endDate: endDate ? toLocalDateString(endDate) : null,
    };
  };

  const getUniqueVoucherCodes = () => {
    if (!redemptionHistory || redemptionHistory.length === 0) return [];
    
    const uniqueCodes = [...new Set(redemptionHistory.map(item => item.voucherCode))];
    return uniqueCodes.sort();
  };

  const updateRedemptionList = useCallback(() => {
    if (!redemptionHistory || redemptionHistory.length === 0) return;
  
    const today = new Date();
    let filteredRedemptions = [];
    let startDate;
    let endDate;
  
    switch (redemptionListView) {
      case "last30days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
  
        filteredRedemptions = redemptionHistory.filter(redemption => {
          const redemptionDate = new Date(redemption.usedAt);
          return redemptionDate >= startDate && redemptionDate <= today;
        });
        break;
  
      case "monthlyPast12":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 11);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
  
        filteredRedemptions = redemptionHistory.filter(redemption => {
          const redemptionDate = new Date(redemption.usedAt);
          return redemptionDate >= startDate && redemptionDate <= today;
        });
        break;
  
      case "yearToDate":
        startDate = new Date(today.getFullYear(), 0, 1);
        startDate.setHours(0, 0, 0, 0);
  
        filteredRedemptions = redemptionHistory.filter(redemption => {
          const redemptionDate = new Date(redemption.usedAt);
          return redemptionDate >= startDate && redemptionDate <= today;
        });
        break;
        
      case "all":
        filteredRedemptions = [...redemptionHistory];
        break;
  
      case "specificMonth":
        if (selectedMonth) {
          const monthParts = selectedMonth.split('-');
          const monthIndex = parseInt(monthParts[0]);
          const monthYear = parseInt(monthParts[1]);
  
          const monthStartDate = new Date(monthYear, monthIndex, 1);
          const monthEndDate = new Date(monthYear, monthIndex + 1, 0, 23, 59, 59, 999);
  
          filteredRedemptions = redemptionHistory.filter(redemption => {
            const redemptionDate = new Date(redemption.usedAt);
            return redemptionDate >= monthStartDate && redemptionDate <= monthEndDate;
          });
        } else {
          filteredRedemptions = [...redemptionHistory];
        }
        break;
  
      case "specificYear":
        if (selectedYear) {
          const yearValue = parseInt(selectedYear);
          const yearStart = new Date(yearValue, 0, 1);
          const yearEnd = new Date(yearValue, 11, 31, 23, 59, 59, 999);
  
          filteredRedemptions = redemptionHistory.filter(redemption => {
            const redemptionDate = new Date(redemption.usedAt);
            return redemptionDate >= yearStart && redemptionDate <= yearEnd;
          });
        } else {
          filteredRedemptions = [...redemptionHistory];
        }
        break;
  
      default:
        filteredRedemptions = [...redemptionHistory];
    }

    if (selectedVoucherCode) {
      filteredRedemptions = filteredRedemptions.filter(redemption => 
        redemption.voucherCode === selectedVoucherCode
      );
    }
  
    const sortedData = filteredRedemptions.sort((a, b) => new Date(b.usedAt) - new Date(a.usedAt));
    const totalRedemptions = filteredRedemptions.length;
    const totalOriginalAmount = filteredRedemptions.reduce((sum, redemption) => sum + redemption.orderTotalAmount, 0);
    const totalDiscountAmount = filteredRedemptions.reduce((sum, redemption) => sum + redemption.discountAmount, 0);
    const totalFinalAmount = filteredRedemptions.reduce((sum, redemption) => sum + (redemption.orderTotalAmount - redemption.discountAmount), 0);
  
    setFilteredRedemptionHistory(sortedData);
    setRedemptionListSummary({
      totalRedemptions,
      totalOriginalAmount,
      totalDiscountAmount,
      totalFinalAmount
    });
    setRedemptionListPage(1);
  }, [redemptionHistory, redemptionListView, selectedMonth, selectedYear, selectedVoucherCode]);

  // update redemption list when view changes or history is loaded
  useEffect(() => {
    updateRedemptionList();
  }, [updateRedemptionList, redemptionListView, selectedMonth, selectedYear, selectedVoucherCode, redemptionHistory]);

  const fetchVoucherOverview = async () => {
    setIsLoading(true);
    
    try {
      const dateParams = getDateRangeParams();
      const queryParams = new URLSearchParams({
        startDate: dateParams.startDate,
        endDate: dateParams.endDate
      }).toString();
  
      const response = await authFetch(`${API_BASE_URL}/api/Vouchers/analytics/overview?${queryParams}`);
      if (!response.ok) {
        throw new Error(`Error fetching voucher overview: ${response.statusText}`);
      }
      const data = await response.json();
      
      setVoucherData(data);
      return data;
    } catch (error) {
      MySwal.fire({
        title: "Error!",
        text: "Failed to load voucher overview data. Please try again.",
        icon: "error",
        confirmButtonColor: "#45a049",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRedemptionData = async () => {
    setLoading(true);
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/Vouchers/analytics/redemptions`);
      if (!response.ok) {
        throw new Error(`Error fetching redemption data: ${response.statusText}`);
      }
      const data = await response.json();
      
      setRedemptionHistory(data);
      return data;
    } catch (error) {
      MySwal.fire({
        title: "Error!",
        text: "Failed to load redemption data. Please try again.",
        icon: "error",
        confirmButtonColor: "#45a049",
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.textContent = `
      .my-swal-container {
        z-index: 20000 !important;
      }
    `;
    document.head.appendChild(styleEl);
  
    const loadInitialData = async () => {
      await fetchVoucherOverview();
      await fetchRedemptionData();
    };
    
    loadInitialData();
  
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Fetch overview data when date range changes
  useEffect(() => {
    if (dateRange === "custom" && (!customDateRange.startDate || !customDateRange.endDate)) {
      return; 
    }
    fetchVoucherOverview();
  }, [dateRange, customDateRange]);

  const handleRefresh = () => {
    fetchVoucherOverview();
    fetchRedemptionData();
    setLastUpdated(new Date());
  };

  const handleResetFilters = () => {
    setSelectedVoucherCode("");
    setSelectedMonth("");
    setSelectedYear("");
    setRedemptionListView("last30days");
  };

  const getPastMonthsOptions = () => {
    const today = new Date();
    const options = [];

    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(today);
      monthDate.setMonth(today.getMonth() - i);

      const monthName = monthDate.toLocaleString('default', { month: 'long' });
      const year = monthDate.getFullYear();

      options.push({
        value: `${monthDate.getMonth()}-${year}`,
        label: `${monthName} ${year}`,
        month: monthDate.getMonth(),
        year: year
      });
    }

    return options;
  };

  const getPastYearsOptions = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const options = [];

    for (let i = 0; i < 5; i++) {
      const year = currentYear - i;
      options.push({
        value: year.toString(),
        label: year.toString(),
        year: year
      });
    }

    return options;
  };

  const indexOfLastRedemption = redemptionListPage * redemptionListPerPage;
  const indexOfFirstRedemption = indexOfLastRedemption - redemptionListPerPage;
  const currentRedemptions = filteredRedemptionHistory.slice(indexOfFirstRedemption, indexOfLastRedemption);
  const totalRedemptionPages = Math.ceil(filteredRedemptionHistory.length / redemptionListPerPage);

  const paginateRedemptionList = (pageNumber) => setRedemptionListPage(pageNumber);

  const handlePrint = () => {
    setIsExportDropdownOpen(false);
    const printFrame = document.createElement('iframe');
    
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    
    document.body.appendChild(printFrame);
    
    let viewTypeDescription = "";
    switch (redemptionListView) {
      case "last30days":
        viewTypeDescription = "Last 30 Days";
        break;
      case "monthlyPast12":
        viewTypeDescription = "Past 12 Months";
        break;
      case "yearToDate":
        viewTypeDescription = "Year to Date";
        break;
      case "specificMonth":
        if (selectedMonth) {
          const monthParts = selectedMonth.split('-');
          const monthIndex = parseInt(monthParts[0]);
          const year = parseInt(monthParts[1]);
          const monthDate = new Date(year, monthIndex, 1);
          viewTypeDescription = `${monthDate.toLocaleString('default', { month: 'long' })} ${year}`;
        } else {
          viewTypeDescription = "Specific Month";
        }
        break;
      case "specificYear":
        viewTypeDescription = selectedYear || "Specific Year";
        break;
      case "all":
        viewTypeDescription = "All Time";
        break;
      default:
        viewTypeDescription = "Custom Period";
    }
    
    let filterDescription = "";
    if (selectedVoucherCode) {
      filterDescription = `Voucher Code: ${selectedVoucherCode}`;
    }
    
    const tableRowsHTML = filteredRedemptionHistory.map((redemption, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${redemption.orderNum}</td>
        <td>${formatDate(redemption.usedAt)}</td>
        <td>${redemption.customerUsername || "Guest"}<br>
          <span class="customer-email">${redemption.customerEmail || ""}</span>
        </td>
        <td>${formatCurrency(redemption.orderTotalAmount)}</td>
        <td>${formatCurrency(redemption.discountAmount)}</td>
        <td>${formatCurrency(redemption.orderTotalAmount - redemption.discountAmount)}</td>
        <td>${redemption.voucherCode}</td>
      </tr>
    `).join('');
    
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Voucher Redemption Report</title>
        <meta charset="UTF-8">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 15px;
            color: #000;
            background-color: white;
            font-size: 12px;
            line-height: 1.3;
          }
          .report-container {
            max-width: 1000px;
            margin: 0 auto;
            padding: 15px;
            background-color: white;
          }
          .printed-on {
            font-size: 11px;
            color: #555;
            margin-bottom: 6px;
          }
          .header-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            border-bottom: 1px solid #000;
            padding-bottom: 10px;
          }
          .company-header {
            max-width: 60%;
          }
          .company-name {
            font-size: 16px;
            font-weight: bold;
            text-transform: uppercase;
            margin-bottom: 2px;
          }
          .company-address {
            font-size: 12px;
          }
          .report-title {
            font-weight: bold;
            font-size: 16px;
            text-align: right;
          }
          .report-subtitle {
            font-size: 14px;
            margin: 10px 0;
            font-weight: bold;
          }
          .report-info {
            margin-bottom: 15px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
          }
          .report-table th, 
          .report-table td {
            padding: 8px;
            border: 1px solid #ddd;
            font-size: 11px;
            text-align: left;
          }
          .report-table th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .customer-email {
            font-size: 10px;
            color: #666;
          }
          .no-data {
            text-align: center;
            padding: 20px;
            font-style: italic;
            color: #666;
          }
          .summary-section {
            margin-top: 20px;
            border-top: 1px solid #ddd;
            padding-top: 15px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          }
          .summary-label {
            font-weight: bold;
          }
          .page-footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #ddd;
            padding-top: 10px;
          }
          .print-date {
            text-align: right;
            font-size: 10px;
            color: #666;
            margin-bottom: 10px;
          }
          @media print {
            /* margin:0 suppresses the browser's own print header/footer (its US-format
               date, the page title and the URL). The page margin is put back on the
               body so the layout is unchanged. */
            @page {
              margin: 0;
            }
            body {
              padding: 12mm 10mm;
              background-color: white;
            }
            .report-container {
              border: none;
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <div class="printed-on">${formatDateTime(new Date())}</div>
          <div class="header-row">
            <div class="company-header">
              <div class="company-name">ARDENCY CONSTRUCTION AND TRADING SDN. BHD.</div>
              <div class="company-address">No.21, Jalan Wawasan 2C/KU7, Sungai Kapar Indah, 42200 Kapar, Selangor</div>
            </div>
            
            <div class="report-title">VOUCHER REPORT</div>
          </div>
          
          <div class="report-info">
            <div class="report-subtitle">Report Period: ${viewTypeDescription}</div>
            ${filterDescription ? `<div>${filterDescription}</div>` : ''}
          </div>
          
          <table class="report-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Order Number</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Original Amount</th>
                <th>Discount Amount</th>
                <th>Final Amount</th>
                <th>Voucher Code</th>
              </tr>
            </thead>
            <tbody>
              ${filteredRedemptionHistory.length > 0 ? tableRowsHTML : 
                '<tr><td colspan="8" class="no-data">No voucher redemptions found for the selected period.</td></tr>'}
            </tbody>
            ${filteredRedemptionHistory.length > 0 ? `
            <tfoot>
              <tr>
                <td colspan="4"><strong>TOTAL</strong></td>
                <td><strong>${formatCurrency(redemptionListSummary.totalOriginalAmount)}</strong></td>
                <td><strong>${formatCurrency(redemptionListSummary.totalDiscountAmount)}</strong></td>
                <td><strong>${formatCurrency(redemptionListSummary.totalFinalAmount)}</strong></td>
                <td></td>
              </tr>
            </tfoot>` : ''}
          </table>
          
          ${filteredRedemptionHistory.length > 0 ? `
          <div class="summary-section">
            <div class="summary-row">
              <span class="summary-label">Total Redemptions:</span>
              <span>${redemptionListSummary.totalRedemptions}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Total Original Amount:</span>
              <span>${formatCurrency(redemptionListSummary.totalOriginalAmount)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Total Discount Amount:</span>
              <span>${formatCurrency(redemptionListSummary.totalDiscountAmount)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Total Final Amount:</span>
              <span>${formatCurrency(redemptionListSummary.totalFinalAmount)}</span>
            </div>
          </div>` : ''}
          
          <div class="page-footer">
            End of Report
          </div>
        </div>
      </body>
      </html>
    `;
    
    const frameDoc = printFrame.contentWindow || printFrame.contentDocument.document || printFrame.contentDocument;
    frameDoc.document.open();
    frameDoc.document.write(receiptHTML);
    frameDoc.document.close();
    
    printFrame.onload = function() {
      try {
        frameDoc.focus();
        frameDoc.print();
        
        setTimeout(function() {
          document.body.removeChild(printFrame);
        }, 1000);
      } catch (e) {
        const originalContent = document.body.innerHTML;
        document.body.innerHTML = receiptHTML;
        window.print();
        document.body.innerHTML = originalContent;
      }
    };
  };

  const handleCsvExport = () => {
    setIsExportDropdownOpen(false);
    
    try {
      let viewTypeDescription = "";
      switch (redemptionListView) {
        case "last30days":
          viewTypeDescription = "Last 30 Days";
          break;
        case "monthlyPast12":
          viewTypeDescription = "Past 12 Months";
          break;
        case "yearToDate":
          viewTypeDescription = "Year to Date";
          break;
        case "specificMonth":
          if (selectedMonth) {
            const monthParts = selectedMonth.split('-');
            const monthIndex = parseInt(monthParts[0]);
            const year = parseInt(monthParts[1]);
            const monthDate = new Date(year, monthIndex, 1);
            viewTypeDescription = `${monthDate.toLocaleString('default', { month: 'long' })} ${year}`;
          } else {
            viewTypeDescription = "Specific Month";
          }
          break;
        case "specificYear":
          viewTypeDescription = selectedYear || "Specific Year";
          break;
        case "all":
          viewTypeDescription = "All Time";
          break;
        default:
          viewTypeDescription = "Custom Period";
      }
      
      let filterDescription = "";
      if (selectedVoucherCode) {
        filterDescription = `Voucher Code: ${selectedVoucherCode}`;
      }
      
      let csvContent = "\ufeff";
      
      csvContent += "ARDENCY CONSTRUCTION AND TRADING SDN. BHD.\n";
      csvContent += "VOUCHER REDEMPTION REPORT\n";
      csvContent += `Report Period: ${viewTypeDescription}\n`;
      if (filterDescription) {
        csvContent += `${filterDescription}\n`;
      }
      csvContent += `Generated on: ${formatDateTime(new Date())}\n\n`;
      
      csvContent += "No.,Order Number,Date,Customer,Customer Email,Original Amount (RM),Discount Amount (RM),Final Amount (RM),Voucher Code\n";
      
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Forces Excel to treat long numeric IDs (e.g. order numbers) as text so they
      // don't get displayed in scientific notation (2.02606E+11).
      const escapeCSVAsText = (value) => {
        if (value === null || value === undefined) return '';
        return `="${String(value).replace(/"/g, '""')}"`;
      };
      
      filteredRedemptionHistory.forEach((redemption, index) => {
        const customerName = redemption.customerUsername || "Guest";
        const customerEmail = redemption.customerEmail || "";
        const originalAmount = parseFloat(redemption.orderTotalAmount).toFixed(2);
        const discountAmount = parseFloat(redemption.discountAmount).toFixed(2);
        const finalAmount = (parseFloat(redemption.orderTotalAmount) - parseFloat(redemption.discountAmount)).toFixed(2);
        
        const rowData = [
          index + 1,
          escapeCSVAsText(redemption.orderNum),
          escapeCSV(formatDate(redemption.usedAt)),
          escapeCSV(customerName),
          escapeCSV(customerEmail),
          originalAmount,
          discountAmount,
          finalAmount,
          escapeCSV(redemption.voucherCode)
        ];
        
        csvContent += rowData.join(',') + "\n";
      });
      
      csvContent += "\nSUMMARY\n";
      csvContent += `Total Redemptions,${redemptionListSummary.totalRedemptions}\n`;
      csvContent += `Total Original Amount (RM),${parseFloat(redemptionListSummary.totalOriginalAmount).toFixed(2)}\n`;
      csvContent += `Total Discount Amount (RM),${parseFloat(redemptionListSummary.totalDiscountAmount).toFixed(2)}\n`;
      csvContent += `Total Final Amount (RM),${parseFloat(redemptionListSummary.totalFinalAmount).toFixed(2)}\n`;
      
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `Ardency_Voucher_Redemption_Report_${toLocalDateString(new Date())}.csv`;
      
      document.body.appendChild(link);
      
      // Trigger the download
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      MySwal.fire({
        title: "Error!",
        text: "Failed to export CSV. Please try again.",
        icon: "error",
        confirmButtonColor: "#45a049",
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="admin-voucher-dashboard-container">
        <div className="admin-voucher-dashboard-loading">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-voucher-dashboard-container">
      {/* Header Section */}
      <div className="admin-voucher-dashboard-header">
        <button
          className="admin-voucher-dashboard-back-btn"
          onClick={() => navigate("/admin/dashboard")}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      {/* Title Section */}
      <div className="admin-voucher-dashboard-title">
        <h1>Voucher Analytics</h1>
      </div>

      {/* Refresh Button */}
      <div className="admin-voucher-dashboard-refresh" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="admin-voucher-dashboard-refresh-btn"
          onClick={handleRefresh}
        >
          <i className="fas fa-sync-alt"></i> Search All
        </button>
        {/* <div className="admin-voucher-dashboard-last-updated">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div> */}
      </div>

      {/* Layout Row for Overview and Reports */}
      <div className="admin-voucher-dashboard-row">
        {/* Voucher Overview Section */}
        <div className="admin-voucher-dashboard-overview">
          <div className="admin-voucher-dashboard-print-section">
            <div className="admin-voucher-dashboard-trends-header">
              <h2>Voucher Overview</h2>
            </div>

            <div className="admin-voucher-dashboard-controls">
              <div className="admin-voucher-dashboard-control-group">
                <label>Date Range</label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last7days">Last 7 Days</option>
                  <option value="last30days">Last 30 Days</option>
                  <option value="monthToDate">Month to Date</option>
                  <option value="quarterToDate">Quarter to Date</option>
                  <option value="yearToDate">Year to Date</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {dateRange === "custom" && (
                <div className="admin-voucher-dashboard-custom-date">
                  <div className="admin-voucher-dashboard-control-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={customDateRange.startDate || ""}
                      onChange={(e) =>
                        setCustomDateRange({
                          ...customDateRange,
                          startDate: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="admin-voucher-dashboard-control-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={customDateRange.endDate || ""}
                      onChange={(e) =>
                        setCustomDateRange({
                          ...customDateRange,
                          endDate: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              )}

              {/* KPIs Section */}
              <div className="admin-voucher-dashboard-kpis" style={{ marginTop: '20px', width: '100%' }}>
                {/* Total Redemptions KPI */}
                <div className="admin-voucher-dashboard-kpi-card">
                  <div className="admin-voucher-dashboard-kpi-title">
                    Total Redemptions
                  </div>
                  <div className="admin-voucher-dashboard-kpi-value">
                    {voucherData.totalRedemptions || 0}
                  </div>
                </div>

                {/* Total Discount Amount KPI */}
                <div className="admin-voucher-dashboard-kpi-card">
                  <div className="admin-voucher-dashboard-kpi-title">
                    Total Discount Amount
                  </div>
                  <div className="admin-voucher-dashboard-kpi-value">
                    {formatCurrency(voucherData.totalDiscountAmount || 0)}
                  </div>
                </div>

                {/* Average Discount Per Order KPI */}
                <div className="admin-voucher-dashboard-kpi-card">
                  <div className="admin-voucher-dashboard-kpi-title">
                    Average Discount Per Order
                  </div>
                  <div className="admin-voucher-dashboard-kpi-value">
                    {formatCurrency(voucherData.averageDiscountPerOrder || 0)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row for Redemption History */}
      <div className="admin-voucher-dashboard-row">
        {/* Redemption History Section */}
        <div className="admin-voucher-dashboard-redemption-container">
          <div className="admin-voucher-dashboard-print-section">
            <div className="admin-voucher-dashboard-trends-header">
              <h2>Voucher Redemption History</h2>
              <div className="admin-voucher-dashboard-trends-controls">
                
              {redemptionListView === "specificMonth" && (
                  <div className="admin-voucher-dashboard-control-group">
                    <label>Select Month</label>
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="admin-voucher-dashboard-month-select"
                    >
                      <option value="">Select a month</option>
                      {getPastMonthsOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {redemptionListView === "specificYear" && (
                  <div className="admin-voucher-dashboard-control-group">
                    <label>Select Year</label>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="admin-voucher-dashboard-year-select"
                    >
                      <option value="">Select a year</option>
                      {getPastYearsOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="admin-voucher-dashboard-control-group">
                  <label>View</label>
                  <select
                    value={redemptionListView}
                    onChange={(e) => {
                      setRedemptionListView(e.target.value);
                      setSelectedMonth("");
                      setSelectedYear("");
                    }}
                  >
                    <option value="last30days">Daily (Past 30 Days)</option>
                    <option value="monthlyPast12">Monthly (Past 12 Months)</option>
                    <option value="yearToDate">Year to Date</option>
                    <option disabled>── Specific Month/Year</option>
                    <option value="specificMonth">Month</option>
                    <option value="specificYear">Year</option>
                  </select>
                </div>
                {/* New Voucher Code Filter */}
                <div className="admin-voucher-dashboard-control-group">
                  <label>Voucher Code</label>
                  <select
                    value={selectedVoucherCode}
                    onChange={(e) => setSelectedVoucherCode(e.target.value)}
                    className="admin-voucher-dashboard-voucher-select"
                  >
                    <option value="">All Voucher Codes</option>
                    {getUniqueVoucherCodes().map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </div>
                 
                <button
                  className="admin-voucher-dashboard-refresh-btn"
                  onClick={handleResetFilters}
                  style={{ height: '35px', marginTop: '22px' }}
                >
                  <i className="fas fa-undo"></i> Search
                </button>
              </div>
            </div>
            
            <div className="admin-voucher-dashboard-table-container">
              {currentRedemptions.length > 0 ? (
                <table className="admin-voucher-dashboard-table">
                  <thead>
                    <tr>
                      <th>Order Number</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Original Amount</th>
                      <th>Discount Amount</th>
                      <th>Final Amount</th>
                      <th>Voucher Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRedemptions.map((redemption) => (
                      <tr key={redemption.voucherUsageId}>
                        <td>#{redemption.orderNum}</td>
                        <td>{formatDate(redemption.usedAt)}</td>
                        <td>
                          <div>{redemption.customerUsername}</div>
                          <div className="admin-voucher-dashboard-email">{redemption.customerEmail}</div>
                        </td>
                        <td>{formatCurrency(redemption.orderTotalAmount)}</td>
                        <td>{formatCurrency(redemption.discountAmount)}</td>
                        <td>{formatCurrency(redemption.orderTotalAmount - redemption.discountAmount)}</td>
                        <td>
                          <span className="admin-voucher-dashboard-code">{redemption.voucherCode}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3"><strong>Total</strong></td>
                      <td><strong>{formatCurrency(redemptionListSummary.totalOriginalAmount)}</strong></td>
                      <td><strong>{formatCurrency(redemptionListSummary.totalDiscountAmount)}</strong></td>
                      <td><strong>{formatCurrency(redemptionListSummary.totalFinalAmount)}</strong></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="admin-voucher-dashboard-empty">
                  <i className="fas fa-ticket-alt"></i>
                  <p className="primary-message">
                    No voucher redemptions found for the selected period.
                  </p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {filteredRedemptionHistory.length > redemptionListPerPage && (
              <div className="admin-voucher-dashboard-pagination">
                <button
                  className="admin-voucher-dashboard-pagination-btn"
                  onClick={() => paginateRedemptionList(1)}
                  disabled={redemptionListPage === 1}
                >
                  <i className="fas fa-angle-double-left"></i>
                </button>
                <button
                  className="admin-voucher-dashboard-pagination-btn"
                  onClick={() => paginateRedemptionList(redemptionListPage - 1)}
                  disabled={redemptionListPage === 1}
                >
                  <i className="fas fa-angle-left"></i>
                </button>

                {[...Array(totalRedemptionPages).keys()].map((number) => {
                  if (
                    number + 1 === 1 ||
                    number + 1 === totalRedemptionPages ||
                    (number + 1 >= redemptionListPage - 1 && number + 1 <= redemptionListPage + 1)
                  ) {
                    return (
                      <button
                        key={number + 1}
                        className={`admin-voucher-dashboard-pagination-btn ${redemptionListPage === number + 1 ? "active" : ""}`}
                        onClick={() => paginateRedemptionList(number + 1)}
                      >
                        {number + 1}
                      </button>
                    );
                  }

                  if (
                    (number + 1 === redemptionListPage - 2 && redemptionListPage > 3) ||
                    (number + 1 === redemptionListPage + 2 && redemptionListPage < totalRedemptionPages - 2)
                  ) {
                    return (
                      <span key={number + 1} className="admin-voucher-dashboard-pagination-btn">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  className="admin-voucher-dashboard-pagination-btn"
                  onClick={() => paginateRedemptionList(redemptionListPage + 1)}
                  disabled={redemptionListPage === totalRedemptionPages}
                >
                  <i className="fas fa-angle-right"></i>
                </button>
                <button
                  className="admin-voucher-dashboard-pagination-btn"
                  onClick={() => paginateRedemptionList(totalRedemptionPages)}
                  disabled={redemptionListPage === totalRedemptionPages}
                >
                  <i className="fas fa-angle-double-right"></i>
                </button>
              </div>
            )}
            <div className="export-dropdown-container">
              <button
                className="admin-voucher-dashboard-print-btn"
                onClick={toggleExportDropdown}
              >
                <i className="fas fa-file-export"></i> Export Report
              </button>

              {isExportDropdownOpen && (
                <div className="export-dropdown-menu">
                  <button
                    className="export-dropdown-item"
                    onClick={handlePrint}
                  >
                    <i className="fas fa-file-pdf"></i> Export as PDF
                  </button>
                  <button
                    className="export-dropdown-item"
                    onClick={handleCsvExport}
                  >
                    <i className="fas fa-file-csv"></i> Export as CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminVoucherAnalytics;